// Phase 4c — top-level `(dashboard …)` op.
//
// The ONLY dashboard op registered in the engine's `opMeta` (hence the
// only one callable at script top level). It dispatches its positional
// tile children through `TILE_TABLE` (context-restricted to
// metric / cartesian / table) and wraps the assembled tiles in the
// lone new `DashboardSpec` Value (V-shape — `ColumnRef`s unresolved,
// no I/O yet). Tiles and sub-forms are never in `opMeta`; they live in
// `dashboardMeta` and are reachable only through this hierarchy.
//
// No kwargs: fixed 12-column grid, no title. Layout is fully
// determined by declaration order + each tile's `:span [W H]`.

import { z } from 'zod';

import type { EvalResult } from '../../env.ts';
import type { AstInput } from '../../normalize.ts';
import {
  createOpTable,
  defineScope,
  type NameTable,
  opMeta,
  opNameOf,
  type Scope,
  type SpecialCtx,
  type SpecialOpMeta,
} from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { DashboardSpec, type Value } from '../../value.ts';
import { type DashboardCtx, dashboardMeta, dispatchDashboardChild } from './dispatch.ts';
import { cartesian } from './tiles/cartesian.ts';
import { metric } from './tiles/metric.ts';
import { table } from './tiles/table.ts';
import type { TileSpec } from './value.ts';

/** Tile ops legal as positional children of `(dashboard …)`. */
const TILE_SCHEMAS = [metric, cartesian, table] as const;

/** Doc + dispatch scope for the tile context. */
export const tileScope: Scope<SpecialOpMeta> = defineScope({
  name: 'Dashboard tiles',
  description: 'Positional children of `(dashboard …)`. Each carries `:span [W H]` in the fixed 12-column grid.',
  registry: dashboardMeta,
  schemas: TILE_SCHEMAS,
});

const TILE_TABLE: NameTable = createOpTable(TILE_SCHEMAS);

export const dashboard = z
  .object({
    name: z.literal('dashboard'),
    params: z.array(z.unknown()).min(1),
  })
  .describe(
    `Assemble a dashboard from tiles (${TILE_SCHEMAS.map(opNameOf).join(', ')}). ` +
      'Fixed 12-column grid; tiles flow left-to-right, top-to-bottom in declaration order; ' +
      'each tile carries `:span [W H]`. Returns a dashboard spec rendered by the host.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    childScopes: [tileScope],
    example: {
      expr: '(dashboard (metric :span [4 2] :value (col w v) :label "X" :format compact) (table (column :ref (col w r) :label "R" :format text) :span [12 4]))',
    },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const baseCtx: DashboardCtx = {
        env: ctx.env,
        spans: ctx.spans,
        path: ctx.path,
        evalCtx: ctx.evalCtx,
        evalNode: ctx.evalNode,
      };
      const tiles = await Promise.all(
        data.params.map((node, i) =>
          dispatchDashboardChild(node as AstInput, TILE_TABLE, baseCtx, paramPath(ctx.path, i)),
        ),
      );
      // `TILE_TABLE` only resolves metric/cartesian/table, so every
      // dispatched child is a `TileSpec` by construction.
      return { value: new DashboardSpec(tiles as readonly TileSpec[]) satisfies Value, env: ctx.env };
    },
  });
